import React, {useState, useEffect, useMemo, useCallback} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Modal, Button, Form, Icon, Header} from 'semantic-ui-react';
import {Actions} from '../../../../common/actions';
import {
  INITIAL_STATE,
  formatAgendaData,
  distributeParticipants,
  getInlineErrors,
} from './agendaCourseModal.utils';
import {useAgendaDataSync} from './agendaCourseModal.hooks';
import GeneralInfoForm from './GeneralInfoForm';
import DealerDistribution from './DealerDistribution';
import AdditionalInfo from './AdditionalInfo';
import moment from 'moment';

const dealersAreEqual = (a, b) => {
  if (a.length !== b.length) {
    return false;
  }
  return a.every((d) => {
    const m = b.find((x) => x.ruc === d.ruc);
    if (!m) {
      return false;
    }
    return Number(m.count) === Number(d.count);
  });
};

const formatDateLong = (dateStr) => {
  if (!dateStr) {
    return '';
  }
  const months = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
  ];
  const d = moment(dateStr, ['DD/MM/YYYY', 'YYYY-MM-DD', moment.ISO_8601]);
  if (!d.isValid()) {
    return dateStr;
  }
  return `${d.date()} de ${months[d.month()]} ${d.year()}`;
};

// eslint-disable-next-line max-statements
const WtsAgendaModal = ({open, onClose, courseToEdit}) => {
  const dispatch = useDispatch();
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState(INITIAL_STATE);
  const [dealerParticipants, setDealerParticipants] = useState([]);
  const [initialDealers, setInitialDealers] = useState([]);
  const [preEvaluacionError, setPreEvaluacionError] = useState(false);

  const reduxData = useSelector((s) => ({
    'areaSelected': s.wtsAreas?.areaSelected,
    'courses': s.wtsCoursesFilter?.list || [],
    'courseDetail': s.wtsCourses?.form,
    'wtsStatus': s.wtsCoursesStatus?.list || [],
    'instructors': s.wtsEmployees?.employeesByCharge || [],
    'wtsDealers': (s.wtsDealer?.dealers || []).filter(
      (d) => d.status === 'ACTIVO' || d.status === 'A'
    ),
    'lastId': s.wtsAgendas?.lastId,
  }));

  const isEditing = useMemo(() => {
    return !!courseToEdit;
  }, [courseToEdit]);

  const COLORS = ['#21ba45', '#2185d0', '#f2711c', '#db2828', '#a333c8'];

  useAgendaDataSync({
    open,
    reduxData,
    dealerParticipants,
    setDealerParticipants,
    setFormData,
    dispatch,
    'actions': Actions,
    'isEditing': isEditing,
  });

  useEffect(() => {
    if (open) {
      if (courseToEdit) {
        setFormData((prev) => {
          if (prev.id === courseToEdit.id) {
            return prev;
          }
          const instList = reduxData.instructors || [];
          const instructorCode = courseToEdit.capacitadores?.length > 0 ?
            courseToEdit.capacitadores[0].employee_code :
            courseToEdit.form;
          const inst = instList.find((i) => Number(i.id) === Number(instructorCode));
          const directed = courseToEdit.directed_to || [];
          return {
            ...prev,
            'id': courseToEdit.id,
            'idCursoReferencia': courseToEdit.course_id,
            'fechaInicio': courseToEdit.start_date,
            'fechaHasta': courseToEdit.end_date,
            'fechaLimiteInscripcion': courseToEdit.inscription_date,
            'fechaLimitePrimeraEvaluacion': courseToEdit.evaluation_date,
            'instructor': inst ? inst.fullName : '',
            'estado': courseToEdit.status,
            'numParticipantes': courseToEdit.participants || 0,
            'descripcion': courseToEdit.course_observation || '',
            'directed_to': directed,
            'dirigidoA': directed,
          };
        });

        const mapped = (courseToEdit.concesionarios || []).map((cd) => {
          const full = reduxData.wtsDealers.find((wd) => wd.ruc === cd.ruc);
          return {
            ...full,
            'ruc': cd.ruc,
            'count': cd.participantes,
            'description': full ? full.description : cd.ruc,
          };
        });

        setDealerParticipants((prev) => {
          const idsMatch = prev.length === mapped.length &&
            prev.every((d, i) => d.ruc === mapped[i]?.ruc);
          return idsMatch ? prev : mapped;
        });
        setInitialDealers(JSON.parse(JSON.stringify(mapped)));
        if (courseToEdit.course_id && reduxData.courseDetail?.id !== courseToEdit.course_id) {
          dispatch(Actions.getWtsCourse(courseToEdit.course_id));
        }
      } else {
        setFormData(INITIAL_STATE);
        setDealerParticipants([]);
        setInitialDealers([]);
        setPreEvaluacionError(false);
        if (reduxData.areaSelected?.id) {
          dispatch(Actions.getWtsAgendaLastId(reduxData.areaSelected.id));
        }
      }
    }
  }, [open, courseToEdit?.id, reduxData.areaSelected?.id]);

  useEffect(() => {
    const detail = reduxData.courseDetail;
    if (detail && String(detail.id) === String(formData.idCursoReferencia)) {
      const nExp = detail.prerequisite || 0;
      const nDir = detail.directed_to || [];
      setFormData((prev) => {
        const strA = JSON.stringify(prev.dirigidoA);
        const strB = JSON.stringify(nDir);
        const hasCh = prev.tiempoExperiencia !== nExp || strA !== strB;
        if (hasCh) {
          return {...prev, 'dirigidoA': nDir, 'directed_to': nDir, 'tiempoExperiencia': nExp};
        }
        return prev;
      });
    }
  }, [reduxData.courseDetail?.id, formData.idCursoReferencia]);

  const handleClose = useCallback(() => {
    onClose();
    setIsSaving(false);
    setFormData(INITIAL_STATE);
    setDealerParticipants([]);
    setInitialDealers([]);
    setPreEvaluacionError(false);
    dispatch(Actions.cleanWtsCourseForm());
  }, [dispatch, onClose]);

  const saveDealers = async(areaId, agendaId) => {
    if (dealersAreEqual(initialDealers, dealerParticipants)) {
      return;
    }
    const deletions = initialDealers.filter(
      (o) => !dealerParticipants.find((c) => c.ruc === o.ruc)
    );
    const updates = dealerParticipants.filter((c) => {
      const o = initialDealers.find((x) => x.ruc === c.ruc);
      return o && Number(o.count) !== Number(c.count);
    });
    const additions = dealerParticipants.filter(
      (c) => !initialDealers.find((o) => o.ruc === c.ruc)
    );
    const promises = [];

    deletions.forEach((d) => {
      promises.push(dispatch(Actions.deleteWtsAgendaDealer(agendaId, areaId, d.ruc)));
    });

    updates.forEach((d) => {
      const data = {'id_agenda': agendaId, 'id_area': areaId, 'participantes': d.count, 'ruc': d.ruc};
      promises.push(dispatch(Actions.updateWtsAgendaDealer(agendaId, areaId, data)));
    });

    additions.forEach((d) => {
      if (d.count > 0) {
        const data = {'id_agenda': agendaId, 'id_area': areaId, 'participantes': d.count, 'ruc': d.ruc};
        promises.push(dispatch(Actions.createWtsAgendaDealer(areaId, areaId, data)));
      }
    });

    await Promise.all(promises);
  };

  // eslint-disable-next-line max-statements
  const handleSubmit = async(e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    const errors = getInlineErrors(formData);
    if (Object.keys(errors).length > 0 || preEvaluacionError || isSaving) {
      return;
    }

    setIsSaving(true);
    try {
      const inst = reduxData.instructors.find((i) => i.fullName === formData.instructor);
      const areaId = reduxData.areaSelected.id;
      const formatted = formatAgendaData(formData, areaId, inst?.id || null);
      
      if (inst) {
        formatted.usuario = inst.user || inst.username || '';
        formatted.codemp = inst.id ? String(inst.id) : '';
      }

      let agendaId = formData.id;

      if (isEditing) {
        await dispatch(Actions.updateWtsAgenda(areaId, formatted));
      } else {
        const resp = await dispatch(Actions.createWtsAgenda(areaId, formatted));
        agendaId = resp?.id || resp?.response?.id || (reduxData.lastId?.ultimo_id + 1);
      }

      if (agendaId) {
        await saveDealers(areaId, agendaId);
      }

      const selectedCourse = (reduxData.courses || []).find(
        (c) => String(c.id) === String(formData.idCursoReferencia)
      );

      // @ts-ignore
      await Promise.all([
        dispatch(Actions.getWtsEmployeeNotifyGes({'area': areaId})),
        dispatch(Actions.getWtsEmployeeNotifyAdm({'area': areaId})),
      ]);

      const freshState = dispatch((_, getState) => getState());
      const gesData = freshState?.wtsEmployeeNotify?.employeeNotifyGes || [];
      const admData = freshState?.wtsEmployeeNotify?.employeeNotifyAdm || [];

      const gesEmails = Array.isArray(gesData) ? gesData.map((emp) => emp.email).filter(Boolean) : [];
      const admEmails = Array.isArray(admData) ? admData.map((emp) => emp.email).filter(Boolean) : [];
      
      const baseNotifyData = {
        'cc_list': admEmails,
        'course_name': selectedCourse?.course || '',
        'end_date': formatDateLong(formData.fechaHasta),
        'evaluation_date': formatDateLong(formData.fechaLimitePrimeraEvaluacion),
        'inscription_date': formatDateLong(formData.fechaLimiteInscripcion),
        'instructor': formData.instructor || '',
        'participants': String(formData.numParticipantes || 0),
        'recipients_list': gesEmails,
        'start_date': formatDateLong(formData.fechaInicio),
      };

      if (!isEditing) {
        setTimeout(() => dispatch(Actions.createCourseNotify({
          'data': {...baseNotifyData, 'tipoEmail': 'creacion_curso'},
        })), 0);
      }

      if (isEditing) {
        setTimeout(() => dispatch(Actions.updateCourseNotify({
          'data': {...baseNotifyData, 'tipoEmail': 'actualizacion_curso'},
        })), 0);

        if (formData.estado === 'X') {
          setTimeout(() => dispatch(Actions.cancelCourseNotify({
            'data': {...baseNotifyData, 'tipoEmail': 'curso_cancelado'},
          })), 0);
        }

        if (formData.estado === 'C') {
          setTimeout(() => dispatch(Actions.closeCourseNotify({
            'data': {...baseNotifyData, 'tipoEmail': 'curso_cerrado'},
          })), 0);
        }
      }

      handleClose();
      dispatch(Actions.getWtsAgendas(areaId));
    } catch (err) {
      setIsSaving(false);
    }
  };

  const statusOptions = useMemo(() => {
    return (reduxData.wtsStatus || [])
      .filter((i) => i.status === 'ACTIVO' || i.status === 'A')
      .map((item, idx) => ({
        'key': item.code,
        'value': item.code,
        'text': item.course_status,
        'content': (
          <Header as="h5" style={{'fontSize': '1rem', 'margin': 0, 'fontWeight': 'normal'}}>
            <Icon name="circle" style={{'color': COLORS[idx % COLORS.length]}} />
            <Header.Content>{item.course_status}</Header.Content>
          </Header>
        ),
      }));
  }, [reduxData.wtsStatus]);

  const currentErrors = getInlineErrors(formData);
  const isFormIncomplete = !formData.idCursoReferencia ||
    !formData.fechaInicio ||
    !formData.fechaHasta ||
    !formData.fechaLimiteInscripcion ||
    !formData.fechaLimitePrimeraEvaluacion;

  const isDisabled = isFormIncomplete ||
    Object.keys(currentErrors).length > 0 ||
    preEvaluacionError ||
    isSaving;

  return (
    <Modal open={open} onClose={handleClose} size="large" closeIcon>
      <Modal.Header>
        <Icon name={isEditing ? 'pencil alternate' : 'add'} />
        {isEditing ? 'Editar Agenda' : 'Agendar Curso'}
      </Modal.Header>
      <Modal.Content scrolling>
        <Form onSubmit={handleSubmit}>
          <GeneralInfoForm
            formData={formData}
            setFormData={setFormData}
            reduxData={reduxData}
            errors={currentErrors}
            statusOptions={statusOptions}
            setPreEvaluacionError={setPreEvaluacionError}
            onCourseChange={(v) => {
              if (v !== formData.idCursoReferencia) {
                setFormData((p) => ({...p, 'idCursoReferencia': v, 'numParticipantes': 0}));
                setDealerParticipants([]);
                setInitialDealers([]);
                dispatch(Actions.getWtsCourse(v));
              }
            }}
            onParticipantsChange={(v) => {
              const t = parseInt(v, 10) || 0;
              setFormData((p) => ({...p, 'numParticipantes': t}));
              setDealerParticipants((prev) => distributeParticipants(t, prev));
            }}
          />
          <DealerDistribution
            dealerParticipants={dealerParticipants}
            wtsDealers={reduxData.wtsDealers}
            onDealerChange={(idx, val) => {
              const upd = [...dealerParticipants];
              upd[idx].count = parseInt(val, 10) || 0;
              setDealerParticipants(upd);
              const totalCount = upd.reduce((a, c) => a + c.count, 0);
              setFormData((p) => ({...p, 'numParticipantes': totalCount}));
            }}
            addDealer={(ev, {value}) => {
              const d = reduxData.wtsDealers.find((x) => x.ruc === value);
              if (d && !dealerParticipants.find((p) => p.ruc === value)) {
                const newList = [...dealerParticipants, {...d, 'count': 0}];
                const distributed = distributeParticipants(formData.numParticipantes, newList);
                setDealerParticipants(distributed);
              }
            }}
            removeDealer={(ruc) => {
              if (dealerParticipants.length > 1) {
                const flt = dealerParticipants.filter((d) => d.ruc !== ruc);
                const distributed = distributeParticipants(formData.numParticipantes, flt);
                setDealerParticipants(distributed);
              }
            }}
            disabled={!formData.idCursoReferencia}
          />
          <AdditionalInfo
            formData={formData}
            setFormData={setFormData}
            reduxData={reduxData}
            disabled={!formData.idCursoReferencia}
          />
        </Form>
      </Modal.Content>
      <Modal.Actions>
        <Button color="red" onClick={handleClose} disabled={isSaving}>
          <Icon name="cancel" /> Cancelar
        </Button>
        <Button
          positive
          onClick={handleSubmit}
          loading={isSaving}
          disabled={isDisabled}
        >
          <Icon name="save" /> {isEditing ? 'Actualizar Agenda' : 'Guardar Agenda'}
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

export default WtsAgendaModal;
